import { computed, reactive } from 'vue'
import { toast } from 'frappe-ui'
import { errorMessage, rpc, rpcGet, unwrap } from './api'

const emptyBoot = () => ({
  authenticated: false,
  user: null,
  full_name: null,
  user_image: null,
  roles: [],
  permissions: [],
  company: null,
  companies: [],
  member: null,
  entitlements: {},
  home_route: '/login',
  is_staff: false,
  is_member: false,
  csrf_token: null,
  can_access_desk: false,
  site_name: null,
  client_base_url: null,
  api_base_url: null,
  desk_url: '/app',
  is_impersonating: false,
  impersonated_by: null,
  original_user: null,
})

export const session = reactive({
  boot: emptyBoot(),
  loaded: false,
  loading: false,
  error: null,
})

export const isLoggedIn = computed(() => !!session.boot?.authenticated)
export const roles = computed(() => session.boot?.roles || [])

function applyBoot(data) {
  // Accept boot object, or login envelope { message, boot }, or nested message
  let payload = data
  if (payload?.boot && payload.authenticated === undefined) {
    payload = payload.boot
  }
  payload = unwrap(payload) || payload
  if (payload?.boot && payload.authenticated === undefined) {
    payload = payload.boot
  }

  session.boot = { ...emptyBoot(), ...(payload || {}) }
  if (session.boot.csrf_token) {
    window.csrf_token = session.boot.csrf_token
  }
  // Prefer server-reported multitenant site name (no hardcoded hosts)
  if (session.boot.site_name) {
    window.site_name = session.boot.site_name
  }
  session.loaded = true
  session.loading = false
  session.error = null
}


/**
 * Portal pages need `boot.member`, but frontend boot does not always include it.
 * Resolve it from the member dashboard once, for any user with portal access.
 */
async function hydrateMember() {
  if (session.boot?.member?.name) return
  if (!session.boot?.authenticated) return
  const permissions = session.boot.permissions || []
  const mayHaveMember = session.boot.is_member || permissions.includes('portal.access')
  if (!mayHaveMember) return
  try {
    const data = await rpc('church_erp.church_erp.utils.user_settings_api.get_member_dashboard', {
      company: session.boot.company || undefined,
    })
    const payload = unwrap(data) || data
    if (payload?.member?.name) session.boot.member = payload.member
  } catch {
    // Optional: staff without a linked member simply have no profile here.
  }
}

export async function fetchBoot(company) {
  session.loading = true
  try {
    const data = await rpcGet(
      'church_erp.church_erp.utils.session_api.get_frontend_boot',
      company ? { company } : {},
    )
    applyBoot(data)
    await hydrateMember()
    return session.boot
  } catch (e) {
    session.error = e
    session.boot = emptyBoot()
    session.loaded = true
    session.loading = false
    throw e
  }
}


let permissionRefresh = null

/**
 * Ask the server whether this parish's overrides (or this user's roles) changed.
 * Only when they did do we re-fetch the full boot payload.
 */
export async function refreshPermissionsIfChanged() {
  if (!session.boot?.authenticated) return false
  if (permissionRefresh) return permissionRefresh
  permissionRefresh = (async () => {
    try {
      const data = await rpcGet(
        'church_erp.church_erp.utils.permissions_api.get_permissions_state',
        {
          company: session.boot.company || undefined,
          branch: session.boot.branch || undefined,
        },
      )
      const payload = unwrap(data) || data
      if (!payload?.version) return false
      const next = [...(payload.permissions || [])].sort()
      const current = [...(session.boot.permissions || [])].sort()
      const changed =
        payload.version !== session.boot.permissions_version ||
        next.length !== current.length ||
        next.some((perm, index) => perm !== current[index])
      if (!changed) return false
      await fetchBoot(session.boot.company || undefined)
      return true
    } catch {
      return false
    } finally {
      permissionRefresh = null
    }
  })()
  return permissionRefresh
}

export async function requestPasswordReset(user) {
  return rpc('church_erp.church_erp.utils.password_reset_api.request_password_reset', { user })
}

export async function validateResetKey(key) {
  return rpcGet('church_erp.church_erp.utils.password_reset_api.validate_reset_key', { key })
}

export async function resetPasswordWithKey(key, newPassword) {
  const data = await rpc('church_erp.church_erp.utils.password_reset_api.reset_password_with_key', {
    key,
    new_password: newPassword,
  })
  if (data?.authenticated) {
    applyBoot(data)
  }
  return data
}

export async function login(usr, pwd, otp, tmpId) {
  // No global session.loading here: it swaps the page for the splash screen,
  // which unmounts Login and wipes its error message (e.g. wrong password, suspended).
  try {
    const data = await rpc(
      'church_erp.church_erp.utils.session_api.frontend_login',
      otp && tmpId ? { otp, tmp_id: tmpId } : { usr, pwd },
    )
    // Staff second factor (Frappe two-factor): no session yet, ask for the code.
    if (data?.two_factor_required) return data
    // Prefer boot object returned as message; always re-fetch if ambiguous
    if (data?.authenticated) {
      applyBoot(data)
    } else if (data?.boot?.authenticated) {
      applyBoot(data.boot)
    } else {
      await fetchBoot()
    }
    if (!session.boot.authenticated) {
      // Cookie may already be set — last chance
      await fetchBoot()
    }
    if (!session.boot.authenticated) {
      throw new Error('Login succeeded but session was not established')
    }
    return session.boot
  } catch (e) {
    session.loading = false
    throw e
  }
}

export async function logout() {
  try {
    await rpc('church_erp.church_erp.utils.session_api.frontend_logout')
  } finally {
    session.boot = emptyBoot()
    session.loaded = true
    session.loading = false
    window.csrf_token = null
  }
}

export async function switchCompany(companyName) {
  const data = await rpc('church_erp.church_erp.utils.session_api.set_frontend_company', {
    company: companyName,
  })
  applyBoot(data)
  return session.boot
}

export function isImpersonating() {
  return !!session.boot?.is_impersonating
}

export async function impersonateUser(user) {
  session.loading = true
  try {
    const data = await rpc('church_erp.church_erp.utils.session_api.start_impersonation', { user })
    if (data?.authenticated) {
      applyBoot(data)
    } else if (data?.boot?.authenticated) {
      applyBoot(data.boot)
    } else {
      await fetchBoot()
    }
    if (!session.boot.authenticated) {
      await fetchBoot()
    }
    return session.boot
  } catch (e) {
    session.loading = false
    throw e
  }
}

export async function endImpersonation() {
  session.loading = true
  try {
    const data = await rpc('church_erp.church_erp.utils.session_api.end_impersonation')
    if (data?.authenticated) {
      applyBoot(data)
    } else if (data?.boot?.authenticated) {
      applyBoot(data.boot)
    } else {
      await fetchBoot()
    }
    if (!session.boot.authenticated) {
      await fetchBoot()
    }
    return session.boot
  } catch (e) {
    session.loading = false
    throw e
  }
}

export function hasRole(...needed) {
  const userRoles = new Set(session.boot?.roles || [])
  if (userRoles.has('System Manager') || session.boot?.user === 'Administrator') return true
  return needed.some((r) => userRoles.has(r))
}

export function hasModule(moduleKey) {
  if (!moduleKey) return true
  if (hasRole('System Manager')) return true
  const mods =
    session.boot?.entitlements?.features?.modules ||
    session.boot?.entitlements?.modules ||
    []
  // No entitlement list means "not loaded/unknown", not "unentitled".
  // Hard-blocking here locked portal members out of every /my page.
  if (!mods.length) return true
  return mods.includes(moduleKey)
}

export function hasAnyRole(list) {
  if (!list?.length) return true
  return hasRole(...list)
}

export function notifyError(e, title = 'Error') {
  toast.error(errorMessage(e) || title)
}

export function notifySuccess(msg) {
  toast.success(msg)
}
